<script lang="ts">
export default {
  name: 'KkSearchKeywordImpressionLabelForm',
}
</script>

<script lang="ts" setup>
import { computed, nextTick } from 'vue'
import { KkFormLabel, KkTextField, KkIcon } from 'src/biz_spa/components/base'
import type { SearchKeywordImpressionLabelKeyword } from '../../../../types'

export interface Props {
  modelValue: SearchKeywordImpressionLabelKeyword[]
}

export interface Emits {
  (key: 'update:modelValue', value: Props['modelValue']): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()

const valueModel = computed({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value),
})

const getNewId = () => {
  const maxId = valueModel.value.reduce((accumulator, element) => Math.max(accumulator, element.id), 1)
  return maxId + 1
}

const addKeyword = () => {
  valueModel.value.push({ id: getNewId(), keyword: '' })
}

const removeKeyword = (id: SearchKeywordImpressionLabelKeyword['id']) => {
  valueModel.value = valueModel.value.filter((keyword) => keyword.id !== id)
  nextTick(() => {
    if (!valueModel.value.length) addKeyword()
  })
}
</script>

<template>
  <div>
    <div v-for="keyword in valueModel" :key="keyword.id" :class="$style.field">
      <KkFormLabel :text="$t('pages.companies.searchKeywordImpressionLabelEdit.word')" :html-for="`word-${keyword.id}`" />
      <div :class="$style.content">
        <KkTextField :id="`word-${keyword.id}`" v-model="keyword.keyword" />
        <button :disabled="valueModel.length === 1 && !keyword.keyword" type="button" :class="$style.remove" @click="removeKeyword(keyword.id)">
          <KkIcon name="delete" color="currentColor" :class="$style.removeIcon" />
          <span :class="$style.removeLabel">{{ $t('labels.delete') }}</span>
        </button>
      </div>
    </div>
  </div>
  <button type="button" :class="$style.add" @click="addKeyword">
    <KkIcon name="add" color="currentColor" :class="$style.addIcon" />
    {{ $t('pages.companies.searchKeywordImpressionLabelEdit.addWord') }}
  </button>
</template>

<style lang="scss" module>
@import "../../../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../../../stylesheets/biz_spa/foundation/mixin";
.field {
  max-width: 638px;
  &:not(:first-child) {
    margin-top: 24px;
  }
}
.content {
  display: flex;
  align-items: center;
  gap: 0 16px;
  @include mq(sm) {
    gap: 0 32px;
  }
}
.remove {
  display: flex;
  align-items: center;
  gap: 0 8px;
  color: #777;
  font-size: 12px;
  white-space: nowrap;
  &:disabled {
    cursor: not-allowed;
  }
}
.removeLabel {
  display: none;
  @include mq(sm) {
    display: inline;
  }
}
.removeIcon {
  width: 16px !important;
}
.add {
  display: flex;
  align-items: center;
  margin-top: 24px;
  color: #E9446A;
  font-size: 12px;
  white-space: nowrap;
  gap: 0 8px;
}
.addIcon {
  width: 16px;
}
</style>
